import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CountUp from 'react-countup';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  Tooltip as ChartTooltip, Legend, Filler,
} from 'chart.js';
import { getRevenusRapport, getVentesRapport } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeResponsiveTable, NdoeDataCard,
  NdoeStatusBadge, NdoeError, NdoeLoading,
} from '../../components/ndoe';
// Pont jetons CSS → canvas : les graphiques lisent la charte au lieu de figer
// des couleurs. Le jour où la charte bascule, ils suivent sans être retouchés.
import { jetons, alpha, optionsBase, fusionner } from '../../components/ndoe/chartTheme';
import DeltaBadge from './DeltaBadge';
import { toCsv, downloadCsv } from './exportCsv';
import { money } from './format';
import './rapports.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ChartTooltip, Legend, Filler);

// Bornes calendaires d'un mois 'YYYY-MM' (pour le drill quotidien).
const bornesMois = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const dernier = new Date(y, m, 0).getDate();
  return { start: `${ym}-01`, end: `${ym}-${String(dernier).padStart(2, '0')}` };
};

export default function RapportRevenus() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [drill, setDrill] = useState(null);   // { mois, libelle, serie|null }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getRevenusRapport({ mois: 24 });
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger le rapport des revenus.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  // Drill quotidien d'un mois : on réutilise le moteur Ventes en période custom.
  const openDrill = async (m) => {
    setDrill({ mois: m.mois, libelle: m.libelle, serie: null });
    try {
      const b = bornesMois(m.mois);
      const { data } = await getVentesRapport({ start_date: b.start, end_date: b.end });
      setDrill((d) => d && { ...d, serie: data.serie_jour, meilleur: data.highlights?.meilleur_jour || null });
    } catch (e) {
      toast.error('Détail du mois indisponible.');
      setDrill(null);
    }
  };

  const handleExport = () => {
    if (!payload) return;
    setExporting(true);
    try {
      const columns = [
        { title: 'Mois', value: (r) => r.libelle },
        { title: 'CA ($)', value: (r) => r.ca },
        { title: 'Évolution vs même mois N-1 (%)', value: (r) => (r.delta_n1_pct === null ? '' : r.delta_n1_pct) },
        { title: 'Paires', value: (r) => r.paires },
        { title: 'Commandes', value: (r) => r.commandes },
        { title: 'Panier moyen ($)', value: (r) => r.panier_moyen },
        { title: 'Marge estimée ($)', value: (r) => r.marge ?? '' },
        { title: 'Couverture marge (%)', value: (r) => r.marge_couverture_pct ?? '' },
      ];
      downloadCsv(`revenus_${payload.meta.start_date}_${payload.meta.end_date}.csv`, toCsv(columns, payload.serie_mensuelle));
      toast.success('Rapport exporté.');
    } catch (e) {
      toast.error("L'export a échoué.");
    } finally { setExporting(false); }
  };

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="Le module Rapports est réservé à l'administrateur." />
      </NdoePage>
    );
  }

  const kpis = payload?.kpis || {};
  const serie = payload?.serie_mensuelle || [];
  const derniers = serie.slice(-12);   // le graphique respire mieux sur 12 mois

  // Jetons lus ICI, dans le corps du rendu : au chargement du module le CSS
  // n'est pas garanti appliqué, getComputedStyle rendrait du vide et le pont
  // retomberait EN SILENCE sur ses valeurs de repli.
  const t = jetons();

  const chartData = {
    labels: derniers.map((m) => m.libelle),
    datasets: [{
      label: 'CA mensuel',
      data: derniers.map((m) => m.ca),
      // Le mois EN COURS est le seul opaque : les 11 mois clos sont du décor de
      // comparaison, c'est le mois vivant qu'on vient lire. L'opacité porte
      // donc l'information, pas une seconde teinte.
      backgroundColor: derniers.map((m, i) => alpha(t.gold, i === derniers.length - 1 ? 0.9 : 0.45)),
      borderRadius: 6,
      maxBarThickness: 42,
    }],
  };
  // optionsBase apporte la grille, les axes, l'info-bulle aux couleurs de la
  // charte et une animation sobre (420 ms, coupée si l'utilisateur a demandé
  // moins d'animations au niveau du système).
  const chartOptions = fusionner(optionsBase(), {
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (i) => ` ${Number(i.parsed.y).toLocaleString('fr-FR')} $` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: t.muted, font: { size: 10 } } },
      y: {
        beginAtZero: true,
        grid: { color: t.hairline, drawTicks: false },
        ticks: { color: t.muted, font: { size: 10.5 }, callback: (v) => `${v} $` },
        border: { display: false },
      },
    },
    onClick: (_e, els) => { if (els.length) openDrill(derniers[els[0].index]); },
  });

  const columns = [
    { title: 'Mois', dataIndex: 'libelle', width: 110, render: (v) => <b>{v}</b> },
    { title: 'CA', dataIndex: 'ca', align: 'right', width: 110, render: (v) => <b className="num">{money(v)}</b> },
    { title: 'vs N-1', dataIndex: 'delta_n1_pct', width: 96, render: (v) => <DeltaBadge value={v} /> },
    { title: 'Paires', dataIndex: 'paires', align: 'right', width: 84, render: (v) => <span className="num">{v}</span> },
    { title: 'Commandes', dataIndex: 'commandes', align: 'right', width: 104, render: (v) => <span className="num">{v}</span> },
    { title: 'Panier', dataIndex: 'panier_moyen', align: 'right', width: 96, render: (v) => <span className="num">{money(v)}</span> },
    {
      title: 'Marge (est.)', dataIndex: 'marge', align: 'right', width: 130,
      render: (v, r) => (v === null
        ? <span className="num pp-marge--inconnue">—</span>
        : <div className="pp-marge"><b className={`num ${v < 0 ? 'is-neg' : ''}`}>{money(v)}</b><small className="num">{r.marge_couverture_pct} % couvert</small></div>),
    },
    {
      title: '', width: 90,
      render: (_, r) => <NdoeActionButton size="sm" variant="ghost" onClick={() => openDrill(r)}>Par jour →</NdoeActionButton>,
    },
  ];

  const renderCard = (m) => (
    <NdoeDataCard
      key={m.mois}
      title={m.libelle}
      subtitle={m.marge !== null ? `marge ${money(m.marge)} (${m.marge_couverture_pct} %)` : 'marge — (coûts non renseignés)'}
      badge={<DeltaBadge value={m.delta_n1_pct} />}
      metrics={[
        { label: 'CA', value: money(m.ca) },
        { label: 'Paires', value: m.paires },
        { label: 'Commandes', value: m.commandes },
        { label: 'Panier', value: money(m.panier_moyen) },
      ]}
      onClick={() => openDrill(m)}
    />
  );

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Revenus"
        subtitle="Le chiffre mois par mois, comparé à l'an dernier — cliquez un mois pour le détail par jour."
        actions={
          <NdoeActionButton variant="secondary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            Exporter CSV
          </NdoeActionButton>
        }
      />

      {error && !loading ? (
        <NdoeError text={error} onRetry={load} />
      ) : loading || !payload ? (
        <NdoeLoading rows={6} />
      ) : (
        <>
          <div className="rv-kpis">
            <div className="rv-kpi rv-kpi--gold">
              <small>Mois en cours ({kpis.mois_courant?.libelle})</small>
              <b className="num"><CountUp end={kpis.mois_courant?.ca || 0} duration={0.9} separator=" " /> $</b>
              <DeltaBadge value={kpis.mois_courant?.delta_n1_pct} />
            </div>
            <div className="rv-kpi">
              <small>Cumul {kpis.ytd?.annee}</small>
              <b className="num"><CountUp end={kpis.ytd?.ca || 0} duration={0.9} separator=" " /> $</b>
              <DeltaBadge value={kpis.ytd?.vs_annee_precedente_pct} />
            </div>
            <div className="rv-kpi">
              <small>Meilleur mois</small>
              <b>{kpis.meilleur_mois?.libelle || '—'}</b>
              <small className="num">{kpis.meilleur_mois ? money(kpis.meilleur_mois.ca) : ''}</small>
            </div>
            <div className="rv-kpi">
              <small>Trimestre glissant</small>
              <b><DeltaBadge value={kpis.trimestre_glissant_pct} /></b>
              <small>3 derniers mois vs 3 précédents</small>
            </div>
          </div>

          <section className="ndoe-card rv-chart">
            <div className="ndoe-section__title">Chiffre d'affaires — 12 derniers mois (cliquez un mois)</div>
            <div className="rv-chart__zone"><Bar data={chartData} options={chartOptions} /></div>
          </section>

          <h3 className="ve-titre">Mois par mois (24 mois)</h3>
          <NdoeResponsiveTable
            className="pp-table"
            data={[...serie].reverse()}
            columns={columns}
            renderCard={renderCard}
            rowKey="mois"
            loading={false}
            scroll={{ x: 860 }}
            pagination={{ pageSize: 12 }}
            empty={{ icon: '📆', title: 'Aucune vente enregistrée', text: '' }}
          />
        </>
      )}

      <Modal open={!!drill} onCancel={() => setDrill(null)} footer={null} width={720} centered
        title={drill ? `Détail quotidien — ${drill.libelle}` : ''}>
        {drill && !drill.serie && <NdoeLoading rows={4} />}
        {drill && drill.serie && (
          <>
            {drill.meilleur && (
              <p className="pp-note">
                <NdoeStatusBadge kind="custom" tone="gold" label="Meilleur jour" />
                {' '}{drill.meilleur.date} — <b className="num">{money(drill.meilleur.ca)}</b> ({drill.meilleur.paires} paires)
              </p>
            )}
            <div style={{ height: 260 }}>
              <Line
                data={{
                  labels: drill.serie.map((j) => j.date.slice(8, 10)),
                  datasets: [{
                    label: 'CA', data: drill.serie.map((j) => j.ca),
                    // Même or que les barres du mois : le détail quotidien est
                    // le zoom d'une barre, pas une autre grandeur.
                    borderColor: t.gold, borderWidth: 2, pointRadius: 2,
                    pointHoverRadius: 5,
                    tension: 0.35, fill: true, backgroundColor: alpha(t.gold, 0.12),
                  }],
                }}
                options={fusionner(optionsBase(), {
                  plugins: { legend: { display: false }, tooltip: { callbacks: { label: (i) => ` ${Number(i.parsed.y).toLocaleString('fr-FR')} $` } } },
                  scales: {
                    x: { grid: { display: false }, ticks: { color: t.muted, font: { size: 10 } } },
                    y: {
                      beginAtZero: true,
                      grid: { color: t.hairline, drawTicks: false },
                      ticks: { color: t.muted, font: { size: 10 } },
                      border: { display: false },
                    },
                  },
                })}
              />
            </div>
          </>
        )}
      </Modal>
    </NdoePage>
  );
}
